import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";

const inputCls =
  "w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 font-body text-sm text-[#f5f5f5] placeholder-[#666] outline-none transition-colors focus:border-[#d4af37]";

export default function AuthPage({ mode = "login" }) {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const u = isLogin ? await login(form.email, form.password) : await register(form.name, form.email, form.password);
      const dest = loc.state?.from || (u.role === "admin" ? "/admin" : "/dashboard");
      nav(dest, { replace: true });
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="hero-particles flex min-h-screen items-center justify-center bg-[#050505] px-6">
      <div className="spotlight pointer-events-none absolute inset-0" />
      <div className="glass-solid gold-border-glow relative z-10 w-full max-w-md rounded-3xl p-8">
        <Link to="/" className="font-display text-2xl font-bold tracking-[0.15em] text-gold" data-testid="auth-logo">BGM</Link>
        <h1 className="mt-5 font-display text-2xl tracking-tighter-x text-[#f5f5f5]">
          {isLogin ? "Welcome back" : "Join the BGM community"}
        </h1>
        <p className="mt-2 font-body text-sm text-[#a3a3a3]">
          {isLogin ? "Log in to your BGM account." : "Create your member account to get started."}
        </p>
        <form onSubmit={submit} className="mt-7 space-y-3" data-testid="auth-form">
          {!isLogin && (
            <input data-testid="auth-name" required placeholder="Full name" value={form.name} onChange={set("name")} className={inputCls} />
          )}
          <input data-testid="auth-email" required type="email" placeholder="Email" value={form.email} onChange={set("email")} className={inputCls} />
          <input data-testid="auth-password" required type="password" placeholder="Password" value={form.password} onChange={set("password")} className={inputCls} />
          {err && <p className="font-body text-xs text-red-400" data-testid="auth-error">{err}</p>}
          {isLogin && (
            <div className="text-right"><Link to="/forgot-password" className="font-body text-xs text-[#888] hover:text-gold" data-testid="auth-forgot-link">Forgot password?</Link></div>
          )}
          <button type="submit" disabled={loading} data-testid="auth-submit"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-[#d4af37] px-6 py-3 font-body text-sm font-semibold text-[#050505] transition-colors hover:bg-[#f3e5ab] disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : isLogin ? "Log in" : "Create account"}
          </button>
        </form>
        <p className="mt-6 text-center font-body text-sm text-[#a3a3a3]">
          {isLogin ? (
            <>New to BGM? <Link to="/register" className="text-gold" data-testid="auth-switch-register">Create an account</Link></>
          ) : (
            <>Already a member? <Link to="/login" className="text-gold" data-testid="auth-switch-login">Log in</Link></>
          )}
        </p>
      </div>
    </div>
  );
}
